import { payoffAt, type PayoffLeg } from '@apna/shared'
import { tradeName } from './useOpenBook'
import { splitBook, type OpenBookTrade } from './useOpenBook'

/**
 * The trades whose risk can be drawn: the SAME split the positions drawer
 * uses, so the curve and the list can never disagree about what is held.
 *
 * A NEEDS_REVIEW trade is included, because since 26 Oct 2026 the drawer
 * draws it. The broker not reporting a leg does not make the risk go away —
 * if anything it is the case where seeing the curve matters most — and a
 * payoff that quietly omitted it would understate the position the trader is
 * looking at two panels away. Never infer holdings from a template.
 */
export function payoffTrades(trades: readonly OpenBookTrade[]): OpenBookTrade[] {
  const { baskets, manual } = splitBook(trades)
  const held = new Set([...baskets, ...manual])
  return trades.filter((t) => t.status !== 'CLOSED' && held.has(t))
}

export function heldPayoff(trade: OpenBookTrade):
  | {
      kind: 'READY'
      legs: PayoffLeg[]
      expiry: string
      units: number
      /** True when later-dated legs were valued at intrinsic — see below. */
      conservative?: boolean
    }
  | { kind: 'UNAVAILABLE'; why: string } {
  const held = trade.legs.filter((l) => l.netQty !== 0)
  if (held.length === 0) return { kind: 'UNAVAILABLE', why: 'This trade no longer holds any open legs.' }
  if (held.some((l) => !Number.isFinite(l.netQty) || !Number.isFinite(l.avgPrice) || l.avgPrice < 0 ||
    !Number.isFinite(l.strike) || l.strike <= 0 || !['CE', 'PE'].includes(l.optionType) ||
    typeof l.expiry !== 'string' || !Number.isFinite(Date.parse(l.expiry)))) {
    return { kind: 'UNAVAILABLE', why: 'An open leg has incomplete contract or entry-price data. No estimated position has been substituted.' }
  }
  const expiries = [...new Set(held.map((l) => l.expiry.slice(0, 10)))].sort()
  const legs = held.map((l) => ({
    netQty: l.netQty,
    avgPrice: l.avgPrice,
    strike: l.strike,
    optionType: l.optionType as 'CE' | 'PE',
  }))

  if (expiries.length === 1) {
    return {
      kind: 'READY',
      expiry: held[0]!.expiry,
      units: held.reduce((n, l) => n + Math.abs(l.netQty), 0),
      legs,
    }
  }

  /*
   * ───────────────────────────────────────────────────────────────────────
   * A CALENDAR CAN STILL BE BOUNDED, AND A BOUND BEATS "NOT KNOWN"
   *
   * This refused every multi-expiry trade on the grounds that "a single
   * expiry curve would misrepresent its risk". True of the curve. Not true
   * of the WORST CASE, for one common shape, and that is worth separating.
   *
   * Read the basket at the NEAREST expiry. A leg expiring that day is worth
   * its intrinsic — exactly what `payoffAt` computes. A leg expiring later
   * is worth intrinsic PLUS time value, and time value is never negative.
   * So pricing every leg as if it expired together moves the answer in a
   * direction that depends only on the leg's SIGN:
   *
   *   LONG far leg    value >= intrinsic, so P&L >= the figure computed.
   *                   The loss is OVERSTATED. A safe, conservative bound.
   *
   *   SHORT far leg   value >= intrinsic and the quantity is negative, so
   *                   P&L <= the figure. The loss is UNDERSTATED, which is
   *                   the one error a risk number must never make.
   *
   * So a long calendar — the common one, and the shape that prompted this —
   * gets a real number marked as an upper bound on the loss. A short-dated
   * sale against a longer-dated purchase is unchanged: still refused, and
   * now for the reason that is actually load-bearing.
   *
   * No volatility, no clock, no model. `maxRisk` stays the exact arithmetic
   * it was; only the caller knows the inputs were pessimised.
   * ───────────────────────────────────────────────────────────────────────
   */
  const near = expiries[0]!
  const later = held.filter((l) => l.expiry.slice(0, 10) > near)
  const shortLater = later.filter((l) => l.netQty < 0)
  if (shortLater.length > 0) {
    return {
      kind: 'UNAVAILABLE',
      why: `This trade is short ${shortLater.length} leg${shortLater.length === 1 ? '' : 's'} expiring after ${near}. Closing those costs their time value, which no expiry curve prices — so any figure would understate the loss.`,
    }
  }

  return {
    kind: 'READY',
    expiry: held.find((l) => l.expiry.slice(0, 10) === near)!.expiry,
    units: held.reduce((n, l) => n + Math.abs(l.netQty), 0),
    legs,
    /*
     * The later legs were priced at intrinsic, so they gave up time value
     * they still hold. Every figure derived from this is a WORST CASE that
     * cannot be beaten downwards, never a prediction.
     */
    conservative: true,
  }
}

/** A one-breakeven call/put needs its LEFT tail classified, not a straddle's middle. */
export function profitableInside(legs: readonly PayoffLeg[], breakevens: readonly number[]): boolean {
  if (breakevens.length === 1) return payoffAt(legs, breakevens[0]! / 2) > 0
  return breakevens.length === 2 && payoffAt(legs, (breakevens[0]! + breakevens[1]!) / 2) > 0
}

/**
 * What to call a trade on the payoff screen.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE NAME IT WAS GIVEN COUNTS, AND IT WAS BEING SKIPPED
 *
 * `strategyName ?? firstHeldLeg.symbol` read straight past `label`, so every
 * trade a trader had NAMED — a folded group, a renamed basket, anything
 * placed as a unit from the chain — appeared in the picker as one of its own
 * contracts. "Long Short Strangle with ATM" listed itself as
 * "NIFTY 25JAN 24100 CE"; a six-leg Iron Condor as whichever leg happened to
 * be first. Reported 2026-11-04.
 *
 * `useOpenBook.tradeName` is the authority and has been right all along —
 * `strategyName ?? label` — and `TradeRow.tsx` carries a comment saying the
 * JOURNAL was corrected to match it back in 4.18. This screen was missed.
 * So this defers to it rather than restating the precedence a third time,
 * and only adds what a picker needs that a card header does not: a fallback
 * for a trade that has no name at all, which is a loose chain click.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function payoffTradeName(trade: OpenBookTrade): string {
  return (
    tradeName(trade) ?? trade.legs.find((l) => l.netQty !== 0)?.symbol ?? trade.underlying
  )
}

export function payoffTradeLabel(trade: OpenBookTrade): string {
  const name = payoffTradeName(trade)
  const opened = new Date(trade.openedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
  return `${name} · ${trade.broker.label} · ${trade.mode} · ${opened}`
}
